import { useAuth } from "@clerk/nextjs";
import classNames from "classnames";
import { LoadingIcon } from "components/LoadingIcon";
import path from "constants/path";
import RecentBookmarkLists from "features/Bookmarks/components/RecentBookmarkLists";
import useLabels from "hooks/useLabels";
import Link from "next/link";
import router, { useRouter } from "next/router";
import History from "../History";

export enum NavigationTab {
  Home = "home",
  ResearchHub = "research-hub",
  History = "history",
  None = "none",
}

export enum NavigationTabVariant {
  Default = "default",
  Small = "sm",
}

interface SidebarNavigationProps {
  onNavigate: () => void;
}

interface SidebarNavigationItem {
  id: NavigationTab | string;
  label: string;
  url: string;
  icon?: string;
  enabled?: boolean;
  onNavigate?: () => void;
  onClick?: () => void;
  isSelected?: boolean;
  variant?: NavigationTabVariant;
  loading?: boolean;
  hoverActions?: React.ReactNode;
}

export function SidebarNavigationItem({
  id,
  label,
  url,
  icon,
  enabled = true,
  onNavigate,
  onClick,
  isSelected = false,
  variant = NavigationTabVariant.Small,
  loading = false,
  hoverActions,
}: SidebarNavigationItem) {
  return variant === NavigationTabVariant.Default ? (
    <div className="w-full flex flex-col" key={id}>
      <button
        style={{ fontVariantLigatures: "none" }}
        className={classNames(
          "flex flex-1 items-center mx-2 px-2 py-1 rounded-xl gap-3 ring-transparent focus:outline-hidden hover:bg-bg-faint",
          isSelected && "bg-bg-faint"
        )}
        disabled={!enabled}
        onClick={() => {
          if (
            window.location.pathname !== url &&
            window.location.pathname !== url + "/"
          ) {
            onNavigate?.();
            router.push(url);
          }
        }}
      >
        {icon && (
          <i className="text-xl">
            <i className={classNames("w-4 h-4 text-fg-base", icon)} />
          </i>
        )}
        <span className="sm-md">{label}</span>
      </button>
      {id === NavigationTab.History && onNavigate ? (
        <History onNavigate={onNavigate} />
      ) : id === NavigationTab.ResearchHub ? (
        <RecentBookmarkLists />
      ) : null}
    </div>
  ) : (
    <li key={id} className="overflow-hidden relative group px-2">
      <div
        className={classNames(
          "flex h-9 pl-2 pr-1 rounded-xl items-center hover:bg-bg-faint",
          isSelected && "bg-bg-faint"
        )}
      >
        <Link href={url} passHref legacyBehavior>
          <a onClick={onClick} className="flex flex-1 text-fg-base sm-normal">
            <span
              className={"flex-1 break-all line-clamp-1 mr-2 text-ellipsis"}
            >
              {label}
            </span>
            {loading && (
              <div className="flex items-center block group-hover:hidden">
                <LoadingIcon className="text-chart-green-base" />
              </div>
            )}
          </a>
        </Link>
        {hoverActions && hoverActions}
      </div>
    </li>
  );
}

function SidebarNavigation({ onNavigate }: SidebarNavigationProps) {
  const [labels] = useLabels("features.sidebar-navigation");
  const router = useRouter();
  const { isSignedIn } = useAuth();

  let selectedTab = NavigationTab.Home;
  if (router.pathname == path.HOME) {
    selectedTab = NavigationTab.Home;
  } else if (router.pathname.includes("/hub")) {
    selectedTab = NavigationTab.ResearchHub;
  } else if (router.pathname.includes("/history")) {
    selectedTab = NavigationTab.History;
  } else {
    selectedTab = NavigationTab.None;
  }

  const navigationTabs: SidebarNavigationItem[] = isSignedIn
    ? [
        {
          id: NavigationTab.Home,
          label: labels["home"],
          icon: "icon-home-converted",
          enabled: true,
          url: "/search",
          onNavigate: onNavigate,
        },
        {
          id: NavigationTab.History,
          label: labels["history"],
          icon: "icon-history",
          enabled: true,
          url: "/history",
          onNavigate: onNavigate,
          isSelected: selectedTab === NavigationTab.History,
        },
        {
          id: NavigationTab.ResearchHub,
          label: labels["research-hub"],
          icon: "icon-folder-converted",
          enabled: true,
          url: "/hub",
          onNavigate: onNavigate,
          isSelected: selectedTab === NavigationTab.ResearchHub,
        },
      ]
    : [
        {
          id: NavigationTab.Home,
          label: labels["home"],
          icon: "icon-home-converted",
          enabled: true,
          url: "/search",
          onNavigate: onNavigate,
        },
      ];

  return (
    <div className="flex flex-col gap-1">
      {navigationTabs.map(
        (tab) =>
          tab.enabled && (
            <SidebarNavigationItem
              key={tab.id}
              variant={NavigationTabVariant.Default}
              {...tab}
            />
          )
      )}
    </div>
  );
}

export default SidebarNavigation;
